import { fieldResolverRegistry } from 'customer-flows/quote/resolvers'

/**
 * Utility function for defining field resolvers with proper typing.
 *
 * This function doesn't actually do anything at runtime; it's purely for
 * type inference and ensuring that resolver definitions are correctly structured.
 */
export function defineResolver<T>(resolverConstructor: ResolverConstructor<T>): ResolverConstructor<T> {
  return resolverConstructor
}

export type ResolverParams = {
  /**
   * Lookup function for reading values from a store using a store name and property key.
   *
   * Example:
   * ```ts
   * const selectedTickets = resolveStoreProp('tickets', 'selectedTickets')
   * ```
   *
   * This is useful when a resolver needs to conditionally derive values from a store,
   * rather than relying on another resolver or static data.
   */
  resolveStoreProp: <T extends keyof StoreRegistry, K extends StoreFieldKey<T>>(_storeName: T, _propertyKey: K) => StoreFieldValue<T, K>
  /**
   * Invoke another field resolver by its key, allowing resolvers to compose fields
   * from multiple sources or reuse logic between resolvers.
   *
   * Example:
   * ```ts
   * const availability = resolveField('availability')
   * ```
   *
   * This enables complex interdependencies without requiring tight coupling
   * between resolvers.
   */
  resolveField: (_key: ResolverKey, _traits?: string[]) => any
  /**
   * Returns the fields from the active flow's `imposesQuoteRequirement` declarations
   * that belong to `store`. Resolvers call this with their own store name to get only
   * the requirements relevant to them — e.g. `requirementsFor('availability')` returns
   * `['selectedDate']` when a date-picker step is present and ignores any requirements
   * from other stores entirely.
   */
  requirementsFor: <S extends keyof StoreRegistry>(store: S) => StoreFieldKey<S>[]
}

/**
 * A function that constructs a resolver object for a specific quote field.
 * This function is invoked with parameters that provide access to store data
 * and other resolvers, enabling dynamic and conditional resolution.
 */
export type ResolverConstructor<T> = (_params: ResolverParams) => Resolver<T>

/**
 * A function that resolves the base value for a specific quote field,
 * typically using input from builder state and store data.
 */
export type FieldResolver<T = any> = () => T

/**
 * A set of named trait functions that modify the resolved field’s value.
 *
 * Traits enable modular composition of variations, enhancing flexibility
 * in how fields are built under different conditions.
 */
export type FieldTraits<T = any> = {
  [key: string]: (_resolvedField: T) => T
}

export type Resolver<T> = {
  /**
   * Main resolver function that produces the field’s initial value.
   */
  resolve: FieldResolver<T>

  /**
   * Optional set of named trait functions for conditional or compositional field shaping.
   */
  traits?: FieldTraits<T>
}

/**
 * Utility type representing the entire field resolver registry structure.
 */
export type FieldResolverRegistry = typeof fieldResolverRegistry

/**
 * Union type of all valid field resolver keys.
 * Used for type-safe access to field resolver functions.
 */
export type ResolverKey = keyof FieldResolverRegistry
export type ResolverType<K extends ResolverKey> = FieldResolverRegistry[K]

export type TraitList = string[]
